import type { QueryClient } from "@tanstack/react-query";
import { encodeScope } from "@/lib/scope";
import type { DashboardQuery, RodzajLeku } from "../api/dashboard-api";
import {
	dashboardForecastQueryOptions,
	dashboardOverviewQueryOptions,
	dashboardQueryFromSearch,
} from "../hooks/dashboard-queries";
import {
	currentDashboardUserId,
	readPersistedDashboardScope,
	resolveDashboardScope,
} from "./dashboard-scope";

export interface DashboardLoaderDeps {
	scope?: string;
	excludedIdfirms?: string;
	dateFrom: string;
	dateTo: string;
	category?: string;
	kosmetyk?: boolean;
	natubay?: boolean;
	rodzaj?: RodzajLeku;
	categories?: string;
	detail?: string;
}

/**
 * Parametry zapytań loadera IDENTYCZNE z tymi, które liczy strona — scope
 * rozstrzygany tą samą funkcją, więc query key prefetchu i hooka są równe
 * i request wykonuje się raz.
 */
export function dashboardLoaderQuery(deps: DashboardLoaderDeps): DashboardQuery {
	const scope = resolveDashboardScope(
		deps.scope,
		readPersistedDashboardScope(currentDashboardUserId()),
	);
	return dashboardQueryFromSearch({
		scope: encodeScope(scope),
		excludedIdfirms: deps.excludedIdfirms,
		dateFrom: deps.dateFrom,
		dateTo: deps.dateTo,
		category: deps.category,
		kosmetyk: deps.kosmetyk,
		natubay: deps.natubay,
		rodzaj: deps.rodzaj,
	});
}

/**
 * `prefetchQuery` IGNORUJE `enabled` (React Query v5) — bez tego gate'u
 * wyłączony widget i tak strzelał w API (pusty `category-detail` kosztował
 * ~11 s w konwoju wejścia na stronę).
 */
function prefetchIfEnabled<T extends { enabled?: unknown }>(
	queryClient: QueryClient,
	options: T,
): void {
	if (options.enabled === false) return;
	void queryClient.prefetchQuery(options as unknown as Parameters<QueryClient["prefetchQuery"]>[0]);
}

/**
 * Initial graph wejścia na dashboard: WYŁĄCZNIE Przegląd i prognoza (ta
 * druga jest tanim tłem i zostaje, gdzie była).
 * Poza grafem zostają:
 *  - Segmenty (od 2026-08-28 liczą korekty partnerskie w 3 okresach, ~0,5 s
 *    więcej na prod), trend 12 m, `price-source`, `central-pricing`,
 *    `stock-kpi`, `category-products`, `category-hierarchy` — gate
 *    widoczności na stronie (`useVisibleOnce`); prefetch tutaj wystrzeliłby
 *    je mimo `enabled`,
 *  - `cheapest-basket` — wyłącznie idle gate na stronie,
 *  - `category-detail` i `cennik-anomalies` — wyłącznie po kliknięciu.
 */
export function prefetchDashboardInitial(
	queryClient: QueryClient,
	deps: DashboardLoaderDeps,
): void {
	const params = dashboardLoaderQuery(deps);
	prefetchIfEnabled(queryClient, dashboardOverviewQueryOptions(params));
	prefetchIfEnabled(queryClient, dashboardForecastQueryOptions(params));
}
